import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "./firebase";
import styles from "./ProductionPredictorDashboard.module.css";

// Unit conversion utilities (ported from React Native predictor)
const UNIT_CONVERSIONS = {
  g: 1,
  kg: 1000,
  lbs: 453.592,
  oz: 28.3495,
  ml: 1,
  liters: 1000,
  cups: 236.588,
  tbsp: 14.7868,
  tsp: 4.92892,
  units: 1,
  pieces: 1,
  boxes: 1,
  packets: 1,
  bottles: 1,
  cans: 1,
  bags: 1,
};

const UNIT_CATEGORIES = {
  weight: ["g", "kg", "lbs", "oz"],
  volume: ["ml", "liters", "cups", "tbsp", "tsp"],
  count: ["units", "pieces", "boxes", "packets", "bottles", "cans", "bags"],
};

const getUnitCategory = (unit) => {
  const normalized = (unit || "").toString().toLowerCase();
  for (const [category, units] of Object.entries(UNIT_CATEGORIES)) {
    if (units.includes(normalized)) return category;
  }
  return null;
};

const convertUnits = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;

  const fromCategory = getUnitCategory(fromUnit);
  const toCategory = getUnitCategory(toUnit);

  if (!fromCategory || !toCategory || fromCategory !== toCategory) {
    return value;
  }

  const fromFactor = UNIT_CONVERSIONS[fromUnit] ?? 1;
  const toFactor = UNIT_CONVERSIONS[toUnit] ?? 1;
  const baseValue = value * fromFactor;
  return baseValue / toFactor;
};

const getBestUnit = (value, unit, getUnitPreferences) => {
  const category = getUnitCategory(unit);
  if (!category) return { value, unit };

  const fromFactor = UNIT_CONVERSIONS[unit] ?? 1;
  const baseValue = value * fromFactor;
  const preferences = getUnitPreferences();

  const preferredUnits = {
    weight:
      preferences.weight.system === "metric"
        ? [
            { unit: "kg", threshold: 1000 },
            { unit: "g", threshold: 0 },
          ]
        : [
            { unit: "lbs", threshold: 453.592 },
            { unit: "oz", threshold: 0 },
          ],
    volume:
      preferences.volume.system === "metric"
        ? [
            { unit: "liters", threshold: 1000 },
            { unit: "ml", threshold: 0 },
          ]
        : [
            { unit: "cups", threshold: 236.588 },
            { unit: "oz", threshold: 29.5735 },
            { unit: "tbsp", threshold: 14.7868 },
            { unit: "tsp", threshold: 0 },
          ],
    count: [{ unit, threshold: 0 }],
  };

  const units = preferredUnits[category] || [{ unit, threshold: 0 }];

  for (const { unit: testUnit, threshold } of units) {
    if (baseValue >= threshold) {
      const toFactor = UNIT_CONVERSIONS[testUnit] ?? 1;
      const convertedValue = baseValue / toFactor;
      return {
        value: Math.round(convertedValue * 100) / 100,
        unit: testUnit,
      };
    }
  }

  return { value: Math.round(value * 100) / 100, unit };
};

export default function ProductionPredictorDashboard() {
  // Web app doesn’t currently have a SettingsContext like the RN app.
  // To retain behavior, we default both weight/volume to metric.
  const getUnitPreferences = useCallback(
    () => ({
      weight: { system: "metric" },
      volume: { system: "metric" },
    }),
    []
  );

  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [combos, setCombos] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedCombo, setSelectedCombo] = useState("");
  const [selectedIngredient, setSelectedIngredient] = useState("");

  const [selectedIngredientStock, setSelectedIngredientStock] = useState(0);
  const [selectedIngredientUnit, setSelectedIngredientUnit] = useState("units");

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [comboPredictions, setComboPredictions] = useState([]);
  const [ingredientProductPredictions, setIngredientProductPredictions] =
    useState([]);
  const [ingredientComboPredictions, setIngredientComboPredictions] = useState(
    []
  );
  const [comboPlan, setComboPlan] = useState({});

  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [maxPackets, setMaxPackets] = useState(0);
  const [maxCombos, setMaxCombos] = useState(0);

  const [activeTab, setActiveTab] = useState("products");

  const [showProductModal, setShowProductModal] = useState(false);
  const [showComboModal, setShowComboModal] = useState(false);
  const [showIngredientModal, setShowIngredientModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const [productsSnap, ingredientsSnap, combosSnap] = await Promise.all([
        getDocs(collection(db, "products")),
        getDocs(collection(db, "ingredients")),
        getDocs(collection(db, "combos")),
      ]);

      setProducts(
        productsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
      setIngredients(
        ingredientsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
      setCombos(combosSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching predictor data:", error);
      setErrorMessage("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getProductRecipe = useCallback((product) => {
    if (!product) {
      return { ingredients: [], basePackets: 1 };
    }

    if (Array.isArray(product.recipes) && product.recipes.length > 0) {
      return product.recipes[0];
    }

    const legacyIngredients = product.recipe || product.ingredients || product.Recipe || [];
    return { ingredients: legacyIngredients, basePackets: 1 };
  }, []);

  const calculateMaxPackets = useCallback(
    (recipe) => {
      if (!recipe?.ingredients?.length) return 0;

      let maxPossible = Infinity;
      const predictionDetails = [];

      recipe.ingredients.forEach((recipeIngredient) => {
        const ingredientData = ingredients.find(
          (ing) =>
            (ing?.name || "").toLowerCase() ===
            (recipeIngredient?.name || "").toLowerCase()
        );

        if (!ingredientData) {
          predictionDetails.push({
            name: recipeIngredient?.name,
            required: recipeIngredient?.quantity || 0,
            requiredUnit: recipeIngredient?.unit || "units",
            available: 0,
            availableUnit: "units",
            maxPackets: 0,
            status: "missing",
          });
          maxPossible = 0;
          return;
        }

        const basePackets = recipe.basePackets || 1;
        const requiredPerPacket = (recipeIngredient?.quantity || 0) / basePackets;
        const requiredUnit = recipeIngredient?.unit || "units";

        const availableStock =
          ingredientData.currentStock ?? ingredientData.stock ?? 0;
        const availableUnit = ingredientData.unit || "units";
        const tracksInventory = ingredientData.trackInventory !== false;

        const requiredBest = getBestUnit(
          requiredPerPacket,
          requiredUnit,
          getUnitPreferences
        );
        const availableBest = getBestUnit(
          availableStock,
          availableUnit,
          getUnitPreferences
        );

        if (!tracksInventory) {
          predictionDetails.push({
            name: recipeIngredient?.name,
            required: requiredBest.value,
            requiredUnit: requiredBest.unit,
            available: "N/A",
            availableUnit: "not tracked",
            maxPackets: Infinity,
            status: "not_tracked",
          });
          return;
        }

        let convertedAvailable = availableStock;

        try {
          if (requiredUnit !== availableUnit) {
            const requiredCategory = getUnitCategory(requiredUnit);
            const availableCategory = getUnitCategory(availableUnit);

            if (
              requiredCategory &&
              availableCategory &&
              requiredCategory === availableCategory
            ) {
              convertedAvailable = convertUnits(
                availableStock,
                availableUnit,
                requiredUnit
              );
            } else {
              predictionDetails.push({
                name: recipeIngredient?.name,
                required: requiredBest.value,
                requiredUnit: requiredBest.unit,
                available: availableBest.value,
                availableUnit: availableBest.unit,
                maxPackets: 0,
                status: "incompatible_units",
              });
              maxPossible = 0;
              return;
            }
          }
        } catch (error) {
          console.error("Unit conversion error:", error);
          predictionDetails.push({
            name: recipeIngredient?.name,
            required: requiredBest.value,
            requiredUnit: requiredBest.unit,
            available: availableBest.value,
            availableUnit: availableBest.unit,
            maxPackets: 0,
            status: "conversion_error",
          });
          maxPossible = 0;
          return;
        }

        const maxForThisIngredient =
          requiredPerPacket > 0
            ? Math.floor(convertedAvailable / requiredPerPacket)
            : Infinity;
        maxPossible = Math.min(maxPossible, maxForThisIngredient);

        predictionDetails.push({
          name: recipeIngredient?.name,
          required: requiredBest.value,
          requiredUnit: requiredBest.unit,
          available: availableBest.value,
          availableUnit: availableBest.unit,
          maxPackets: maxForThisIngredient,
          status: maxForThisIngredient > 0 ? "available" : "insufficient",
        });
      });

      const finalMax = maxPossible === Infinity ? 0 : Math.max(0, maxPossible);
      setMaxPackets(finalMax);
      setPredictions(predictionDetails);

      return finalMax;
    },
    [ingredients, getUnitPreferences]
  );

  const calculateComboMaxPackets = useCallback(
    (combo) => {
      if (!combo?.products?.length) {
        return { maxCombos: 0, predictions: [] };
      }

      let maxPossibleCombos = Infinity;
      const comboPredictionDetails = [];

      combo.products.forEach((comboProduct) => {
        const productData = products.find((p) => p.id === comboProduct.productId);

        if (!productData) {
          comboPredictionDetails.push({
            productName: comboProduct.productName || "Unknown Product",
            productId: comboProduct.productId,
            requiredPerCombo: comboProduct.quantity,
            maxPacketsCanMake: 0,
            maxCombos: 0,
            status: "missing_product",
          });
          maxPossibleCombos = 0;
          return;
        }

        const recipe = getProductRecipe(productData);
        const maxPacketsForRecipe = calculateMaxPackets(recipe);

        const requiredPerCombo = comboProduct.quantity || 0;
        const maxCombosFromThisProduct =
          requiredPerCombo > 0
            ? Math.floor(maxPacketsForRecipe / requiredPerCombo)
            : Infinity;

        maxPossibleCombos = Math.min(maxPossibleCombos, maxCombosFromThisProduct);

        comboPredictionDetails.push({
          productName: comboProduct.productName || productData.name,
          productId: comboProduct.productId,
          requiredPerCombo,
          maxPacketsCanMake: maxPacketsForRecipe,
          maxCombos: maxCombosFromThisProduct,
          status:
            maxCombosFromThisProduct > 0
              ? "available"
              : maxPacketsForRecipe === 0
                ? "cannot_produce"
                : "insufficient_packets",
        });
      });

      const finalMaxCombos =
        maxPossibleCombos === Infinity ? 0 : Math.max(0, maxPossibleCombos);

      return { maxCombos: finalMaxCombos, predictions: comboPredictionDetails };
    },
    [products, calculateMaxPackets, getProductRecipe]
  );

  const calculateProductsFromIngredient = useCallback(
    (ingredientData) => {
      if (!ingredientData) {
        setIngredientProductPredictions([]);
        setIngredientComboPredictions([]);
        return [];
      }

      const ingredientName = ingredientData.name || "";
      const availableStock =
        ingredientData.currentStock ?? ingredientData.stock ?? 0;
      const availableUnit = ingredientData.unit || "units";
      const tracksInventory = ingredientData.trackInventory !== false;

      const availableBest = getBestUnit(
        availableStock,
        availableUnit,
        getUnitPreferences
      );

      const candidates = [];

      products.forEach((product) => {
        const recipe = getProductRecipe(product);
        if (!recipe?.ingredients?.length) return;

        const recipeIngredient = recipe.ingredients.find(
          (ing) =>
            (ing?.name || "").toLowerCase() === ingredientName.toLowerCase()
        );
        if (!recipeIngredient) return;

        const basePackets = recipe.basePackets || 1;
        const requiredPerPacket = (recipeIngredient.quantity || 0) / basePackets;
        const requiredUnit = recipeIngredient.unit || "units";

        const requiredBest = getBestUnit(
          requiredPerPacket,
          requiredUnit,
          getUnitPreferences
        );

        if (!tracksInventory) {
          candidates.push({
            productId: product.id,
            productName: product.name,
            ingredientName,
            required: requiredBest.value,
            requiredUnit: requiredBest.unit,
            available: "N/A",
            availableUnit: "not tracked",
            maxPackets: Infinity,
            status: "not_tracked",
          });
          return;
        }

        let status = "available";
        try {
          if (requiredUnit !== availableUnit) {
            const requiredCategory = getUnitCategory(requiredUnit);
            const availableCategory = getUnitCategory(availableUnit);
            if (
              !(
                requiredCategory &&
                availableCategory &&
                requiredCategory === availableCategory
              )
            ) {
              status = "incompatible_units";
            }
          }
        } catch (error) {
          console.error("Unit conversion error:", error);
          status = "conversion_error";
        }

        candidates.push({
          productId: product.id,
          productName: product.name,
          ingredientName,
          required: requiredBest.value,
          requiredUnit: requiredBest.unit,
          available: availableBest.value,
          availableUnit: availableBest.unit,
          requiredPerPacket,
          requiredUnitRaw: requiredUnit,
          availableStock,
          availableUnitRaw: availableUnit,
          status,
        });
      });

      const compatible = candidates.filter((c) => c.status === "available");
      const shareCount = compatible.length;

      let availableBase = 0;
      if (shareCount > 0) {
        const unitConv = UNIT_CONVERSIONS[availableUnit] || 1;
        availableBase = (availableStock || 0) * unitConv;
      }

      const results = candidates
        .map((c) => {
          if (c.status === "not_tracked") {
            return { ...c, maxPackets: Infinity, status: "not_tracked" };
          }
          if (c.status !== "available") {
            return { ...c, maxPackets: 0, status: c.status };
          }
          if (shareCount <= 0) {
            return { ...c, maxPackets: 0, status: "insufficient" };
          }

          const shareBase = availableBase / shareCount;
          const requiredConv = UNIT_CONVERSIONS[c.requiredUnitRaw] || 1;
          const requiredPerPacketBase = (c.requiredPerPacket || 0) * requiredConv;

          const maxPacketsParallel =
            requiredPerPacketBase > 0
              ? Math.floor(shareBase / requiredPerPacketBase)
              : Infinity;

          return {
            ...c,
            maxPackets: maxPacketsParallel,
            status: maxPacketsParallel > 0 ? "available" : "insufficient",
          };
        })
        .sort((a, b) => {
          const aVal =
            a.maxPackets === Infinity ? Number.MAX_SAFE_INTEGER : a.maxPackets;
          const bVal =
            b.maxPackets === Infinity ? Number.MAX_SAFE_INTEGER : b.maxPackets;
          if (bVal !== aVal) return bVal - aVal;
          return (a.productName || "").localeCompare(b.productName || "");
        });

      setIngredientProductPredictions(results);

      const comboCandidates = [];

      combos.forEach((combo) => {
        if (!combo?.products?.length) return;

        let requiredPerComboUnits = 0;
        let status = "available";

        for (const comboProduct of combo.products) {
          const productData = products.find((p) => p.id === comboProduct.productId);
          if (!productData) {
            status = "missing_product";
            break;
          }

          const recipe = getProductRecipe(productData);
          if (!recipe?.ingredients?.length) continue;

          const recipeIngredient = recipe.ingredients.find(
            (ing) =>
              (ing?.name || "").toLowerCase() === ingredientName.toLowerCase()
          );
          if (!recipeIngredient) continue;

          const basePackets = recipe.basePackets || 1;
          const requiredPerPacket = (recipeIngredient.quantity || 0) / basePackets;
          const requiredUnit = recipeIngredient.unit || "units";

          try {
            if (requiredUnit !== availableUnit) {
              const requiredCategory = getUnitCategory(requiredUnit);
              const availableCategory = getUnitCategory(availableUnit);

              if (
                requiredCategory &&
                availableCategory &&
                requiredCategory === availableCategory
              ) {
                const converted = convertUnits(
                  requiredPerPacket,
                  requiredUnit,
                  availableUnit
                );

                requiredPerComboUnits +=
                  (converted || 0) * (comboProduct.quantity || 0);
              } else {
                status = "incompatible_units";
                break;
              }
            } else {
              requiredPerComboUnits +=
                (requiredPerPacket || 0) * (comboProduct.quantity || 0);
            }
          } catch (error) {
            console.error("Unit conversion error (combo):", error);
            status = "conversion_error";
            break;
          }
        }

        const availableBestForDisplay = getBestUnit(
          availableStock,
          availableUnit,
          getUnitPreferences
        );

        if (status !== "available") {
          comboCandidates.push({
            comboId: combo.id,
            comboName: combo.name,
            requiredPerComboUnits,
            requiredUnitDisplay: availableBestForDisplay.unit,
            available: availableBestForDisplay.value,
            availableUnit: availableBestForDisplay.unit,
            status,
            maxCombos: 0,
          });
          return;
        }

        if (requiredPerComboUnits <= 0) return;

        comboCandidates.push({
          comboId: combo.id,
          comboName: combo.name,
          requiredPerComboUnits,
          requiredUnitDisplay: availableBestForDisplay.unit,
          available: availableBestForDisplay.value,
          availableUnit: availableBestForDisplay.unit,
          status: "available",
          maxCombos: 0,
        });
      });

      const comboCompat = comboCandidates.filter((c) => c.status === "available");
      const comboShareCount = comboCompat.length;
      const availableShareUnits =
        comboShareCount > 0 ? (availableStock || 0) / comboShareCount : 0;

      const comboResults = comboCandidates
        .map((c) => {
          if (c.status !== "available") return { ...c, maxCombos: 0, status: c.status };
          if (comboShareCount <= 0) return { ...c, maxCombos: 0, status: "insufficient" };

          const maxCombosParallel =
            c.requiredPerComboUnits > 0
              ? Math.floor(availableShareUnits / c.requiredPerComboUnits)
              : Infinity;

          return {
            ...c,
            maxCombos: maxCombosParallel,
            status: maxCombosParallel > 0 ? "available" : "insufficient",
          };
        })
        .sort((a, b) => {
          const aVal = a.maxCombos === Infinity ? Number.MAX_SAFE_INTEGER : a.maxCombos;
          const bVal = b.maxCombos === Infinity ? Number.MAX_SAFE_INTEGER : b.maxCombos;
          if (bVal !== aVal) return bVal - aVal;
          return (a.comboName || "").localeCompare(b.comboName || "");
        });

      setIngredientComboPredictions(comboResults);
      setComboPlan(
        comboResults.reduce((acc, row) => {
          if (row.comboId) acc[row.comboId] = 0;
          return acc;
        }, {})
      );

      return results;
    },
    [products, combos, getProductRecipe, getUnitPreferences]
  );

  const computeIngredientAnalysis = useCallback(
    (ingredientData) => {
      if (!ingredientData) return { products: [], combos: [] };

      const ingredientName = ingredientData.name || "";
      const availableStock =
        ingredientData.currentStock ?? ingredientData.stock ?? 0;
      const availableUnit = ingredientData.unit || "units";
      const tracksInventory = ingredientData.trackInventory !== false;

      const availableBest = getBestUnit(
        availableStock,
        availableUnit,
        getUnitPreferences
      );

      const candidates = [];
      products.forEach((product) => {
        const recipe = getProductRecipe(product);
        if (!recipe?.ingredients?.length) return;

        const recipeIngredient = recipe.ingredients.find(
          (ing) =>
            (ing?.name || "").toLowerCase() === ingredientName.toLowerCase()
        );
        if (!recipeIngredient) return;

        const basePackets = recipe.basePackets || 1;
        const requiredPerPacket = (recipeIngredient.quantity || 0) / basePackets;
        const requiredUnit = recipeIngredient.unit || "units";

        const requiredBest = getBestUnit(
          requiredPerPacket,
          requiredUnit,
          getUnitPreferences
        );

        if (!tracksInventory) {
          candidates.push({
            productId: product.id,
            productName: product.name,
            ingredientName,
            required: requiredBest.value,
            requiredUnit: requiredBest.unit,
            available: "N/A",
            availableUnit: "not tracked",
            maxPackets: Infinity,
            status: "not_tracked",
          });
          return;
        }

        let status = "available";
        try {
          if (requiredUnit !== availableUnit) {
            const requiredCategory = getUnitCategory(requiredUnit);
            const availableCategory = getUnitCategory(availableUnit);
            if (
              !(
                requiredCategory &&
                availableCategory &&
                requiredCategory === availableCategory
              )
            ) {
              status = "incompatible_units";
            }
          }
        } catch {
          status = "conversion_error";
        }

        candidates.push({
          productId: product.id,
          productName: product.name,
          ingredientName,
          required: requiredBest.value,
          requiredUnit: requiredBest.unit,
          available: availableBest.value,
          availableUnit: availableBest.unit,
          requiredPerPacket,
          requiredUnitRaw: requiredUnit,
          availableStock,
          availableUnitRaw: availableUnit,
          status,
        });
      });

      const compatible = candidates.filter((c) => c.status === "available");
      const shareCount = compatible.length;

      let availableBase = 0;
      if (shareCount > 0) {
        const unitConv = UNIT_CONVERSIONS[availableUnit] || 1;
        availableBase = (availableStock || 0) * unitConv;
      }

      const productResults = candidates
        .map((c) => {
          if (c.status === "not_tracked") return { ...c, maxPackets: Infinity };
          if (c.status !== "available") return { ...c, maxPackets: 0 };
          if (shareCount <= 0) return { ...c, maxPackets: 0, status: "insufficient" };

          const shareBase = availableBase / shareCount;
          const requiredConv = UNIT_CONVERSIONS[c.requiredUnitRaw] || 1;
          const requiredPerPacketBase = (c.requiredPerPacket || 0) * requiredConv;
          const maxPacketsParallel =
            requiredPerPacketBase > 0
              ? Math.floor(shareBase / requiredPerPacketBase)
              : Infinity;

          return {
            ...c,
            maxPackets: maxPacketsParallel,
            status: maxPacketsParallel > 0 ? "available" : "insufficient",
          };
        })
        .sort((a, b) => {
          const aVal = a.maxPackets === Infinity ? Number.MAX_SAFE_INTEGER : a.maxPackets;
          const bVal = b.maxPackets === Infinity ? Number.MAX_SAFE_INTEGER : b.maxPackets;
          if (bVal !== aVal) return bVal - aVal;
          return (a.productName || "").localeCompare(b.productName || "");
        });

      const comboCandidates = [];
      combos.forEach((combo) => {
        if (!combo?.products?.length) return;

        let requiredPerComboUnits = 0;
        let status = "available";

        for (const comboProduct of combo.products) {
          const productData = products.find((p) => p.id === comboProduct.productId);
          if (!productData) {
            status = "missing_product";
            break;
          }

          const recipe = getProductRecipe(productData);
          if (!recipe?.ingredients?.length) continue;

          const recipeIngredient = recipe.ingredients.find(
            (ing) =>
              (ing?.name || "").toLowerCase() === ingredientName.toLowerCase()
          );
          if (!recipeIngredient) continue;

          const basePackets = recipe.basePackets || 1;
          const requiredPerPacket = (recipeIngredient.quantity || 0) / basePackets;
          const requiredUnit = recipeIngredient.unit || "units";

          try {
            if (requiredUnit !== availableUnit) {
              const requiredCategory = getUnitCategory(requiredUnit);
              const availableCategory = getUnitCategory(availableUnit);
              if (
                requiredCategory &&
                availableCategory &&
                requiredCategory === availableCategory
              ) {
                const converted = convertUnits(
                  requiredPerPacket,
                  requiredUnit,
                  availableUnit
                );
                requiredPerComboUnits += (converted || 0) * (comboProduct.quantity || 0);
              } else {
                status = "incompatible_units";
                break;
              }
            } else {
              requiredPerComboUnits += (requiredPerPacket || 0) * (comboProduct.quantity || 0);
            }
          } catch {
            status = "conversion_error";
            break;
          }
        }

        if (status !== "available") {
          comboCandidates.push({
            comboId: combo.id,
            comboName: combo.name,
            requiredPerComboUnits,
            requiredUnitDisplay: availableBest.unit,
            available: availableBest.value,
            availableUnit: availableBest.unit,
            status,
            maxCombos: 0,
          });
          return;
        }

        if (requiredPerComboUnits <= 0) return;

        comboCandidates.push({
          comboId: combo.id,
          comboName: combo.name,
          requiredPerComboUnits,
          requiredUnitDisplay: availableBest.unit,
          available: availableBest.value,
          availableUnit: availableBest.unit,
          status: "available",
          maxCombos: 0,
        });
      });

      const comboCompat = comboCandidates.filter((c) => c.status === "available");
      const comboShareCount = comboCompat.length;
      const availableShareUnits =
        comboShareCount > 0 ? (availableStock || 0) / comboShareCount : 0;

      const comboResults = comboCandidates
        .map((c) => {
          if (c.status !== "available") return { ...c, maxCombos: 0 };
          if (comboShareCount <= 0) return { ...c, maxCombos: 0, status: "insufficient" };
          const maxCombosParallel =
            c.requiredPerComboUnits > 0
              ? Math.floor(availableShareUnits / c.requiredPerComboUnits)
              : Infinity;

          return {
            ...c,
            maxCombos: maxCombosParallel,
            status: maxCombosParallel > 0 ? "available" : "insufficient",
          };
        })
        .sort((a, b) => {
          const aVal = a.maxCombos === Infinity ? Number.MAX_SAFE_INTEGER : a.maxCombos;
          const bVal = b.maxCombos === Infinity ? Number.MAX_SAFE_INTEGER : b.maxCombos;
          if (bVal !== aVal) return bVal - aVal;
          return (a.comboName || "").localeCompare(b.comboName || "");
        });

      return {
        products: productResults,
        combos: comboResults,
        availableBest,
        availableUnit,
        availableStock,
      };
    },
    [products, combos, getProductRecipe, getUnitPreferences]
  );

  const exportAllIngredientsToXLS = useCallback(async () => {
    try {
      setExporting(true);
      setErrorMessage("");

      if (!ingredients.length) {
        setErrorMessage("No ingredients found. Please sync ingredients first.");
        return;
      }

      const productRows = [];
      const comboRows = [];

      ingredients.forEach((ingredient) => {
        const { products: prodRes, combos: combRes } =
          computeIngredientAnalysis(ingredient);

        prodRes.forEach((p) => {
          productRows.push({
            Ingredient: ingredient.name,
            Available: p.available,
            "Available Unit": p.availableUnit,
            Product: p.productName,
            "Required per Packet": p.required,
            "Required Unit": p.requiredUnit,
            "Max Packets (parallel)": p.maxPackets === Infinity ? "∞" : p.maxPackets,
            Status: p.status,
          });
        });

        combRes.forEach((c) => {
          comboRows.push({
            Ingredient: ingredient.name,
            Available: c.available,
            "Available Unit": c.availableUnit,
            Combo: c.comboName,
            "Required per Combo (ingredient units)":
              Math.round((c.requiredPerComboUnits || 0) * 100) / 100,
            "Required Unit": c.requiredUnitDisplay,
            "Max Combos (parallel)": c.maxCombos === Infinity ? "∞" : c.maxCombos,
            Status: c.status,
          });
        });
      });

      const wb = XLSX.utils.book_new();
      const productSheet = XLSX.utils.json_to_sheet(
        productRows.length ? productRows : [{ Note: "No product predictions available" }]
      );
      const comboSheet = XLSX.utils.json_to_sheet(
        comboRows.length ? comboRows : [{ Note: "No combo predictions available" }]
      );

      XLSX.utils.book_append_sheet(wb, productSheet, "Products by Ingredient");
      XLSX.utils.book_append_sheet(wb, comboSheet, "Combos by Ingredient");

      XLSX.writeFile(wb, "ingredient_predictions.xlsx");
    } catch (error) {
      console.error("Export error:", error);
      setErrorMessage("Export failed. Could not generate the XLS file.");
    } finally {
      setExporting(false);
    }
  }, [ingredients, computeIngredientAnalysis]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "available":
        return "✓";
      case "not_tracked":
        return "ℹ️";
      case "insufficient":
        return "⚠️";
      case "missing":
        return "❌";
      case "incompatible_units":
      case "conversion_error":
        return "⚠️";
      default:
        return "?";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available":
        return "#10b981";
      case "not_tracked":
        return "#06b6d4";
      case "insufficient":
        return "#f59e0b";
      case "missing":
      case "conversion_error":
        return "#ef4444";
      case "incompatible_units":
        return "#f97316";
      default:
        return "#6b7280";
    }
  };

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    setSelectedCombo("");
    setSelectedIngredient("");
    setShowProductModal(false);

    const product = products.find((p) => p.id === productId);
    if (product) {
      const recipe = getProductRecipe(product);
      setSelectedRecipe(recipe);
      calculateMaxPackets(recipe);
    } else {
      setSelectedRecipe(null);
      setPredictions([]);
      setMaxPackets(0);
    }
  };

  const handleComboSelect = (comboId) => {
    setSelectedCombo(comboId);
    setSelectedProduct("");
    setSelectedIngredient("");
    setShowComboModal(false);

    const combo = combos.find((c) => c.id === comboId);
    if (combo) {
      const { maxCombos: maxC, predictions: preds } = calculateComboMaxPackets(combo);
      setMaxCombos(maxC);
      setComboPredictions(preds);
    } else {
      setComboPredictions([]);
      setMaxCombos(0);
    }
  };

  const handleIngredientSelect = (ingredientId) => {
    setSelectedIngredient(ingredientId);
    setSelectedProduct("");
    setSelectedCombo("");
    setShowIngredientModal(false);

    const ingredientData = ingredients.find((i) => i.id === ingredientId);
    const stock = ingredientData?.currentStock ?? ingredientData?.stock ?? 0;
    const unit = ingredientData?.unit || "units";

    setSelectedIngredientStock(stock);
    setSelectedIngredientUnit(unit);

    calculateProductsFromIngredient(ingredientData);
  };

  const adjustComboPlan = (comboId, delta) => {
    setComboPlan((prev) => {
      const current = prev?.[comboId] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [comboId]: next };
    });
  };

  const setComboPlanValue = (comboId, value) => {
    const parsed = parseFloat(value);
    const safe = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    setComboPlan((prev) => ({ ...prev, [comboId]: safe }));
  };

  const selectedProductName = useMemo(() => {
    if (!selectedProduct) return "Choose a product...";
    const product = products.find((p) => p.id === selectedProduct);
    return product ? product.name : "Choose a product...";
  }, [selectedProduct, products]);

  const selectedComboName = useMemo(() => {
    if (!selectedCombo) return "Choose a combo...";
    const combo = combos.find((c) => c.id === selectedCombo);
    return combo ? combo.name : "Choose a combo...";
  }, [selectedCombo, combos]);

  const selectedIngredientName = useMemo(() => {
    if (!selectedIngredient) return "Choose an ingredient...";
    const ing = ingredients.find((i) => i.id === selectedIngredient);
    return ing ? ing.name : "Choose an ingredient...";
  }, [selectedIngredient, ingredients]);

  const productChoices = useMemo(() => {
    const validProducts = products
      .filter((product) => {
        const recipe = getProductRecipe(product);
        return recipe?.ingredients?.length;
      })
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return [
      { id: "", name: "Choose a product...", isPlaceholder: true },
      ...validProducts,
    ];
  }, [products, getProductRecipe]);

  const comboChoices = useMemo(() => {
    const sorted = combos
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return [{ id: "", name: "Choose a combo...", isPlaceholder: true }, ...sorted];
  }, [combos]);

  const ingredientChoices = useMemo(() => {
    const sorted = ingredients
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return [
      { id: "", name: "Choose an ingredient...", isPlaceholder: true },
      ...sorted,
    ];
  }, [ingredients]);

  const totalProductPacketsParallel = useMemo(() => {
    if (ingredientProductPredictions.some((r) => r.maxPackets === Infinity)) {
      return "∞";
    }

    const total = ingredientProductPredictions.reduce(
      (sum, r) => sum + (Number.isFinite(r.maxPackets) ? r.maxPackets : 0),
      0
    );
    return total;
  }, [ingredientProductPredictions]);

  const totalComboParallel = useMemo(() => {
    if (ingredientComboPredictions.some((r) => r.maxCombos === Infinity)) {
      return "∞";
    }

    const total = ingredientComboPredictions.reduce(
      (sum, r) => sum + (Number.isFinite(r.maxCombos) ? r.maxCombos : 0),
      0
    );
    return total;
  }, [ingredientComboPredictions]);

  const totalReservedIngredient = useMemo(() => {
    return ingredientComboPredictions.reduce((sum, row) => {
      const planned = comboPlan[row.comboId] || 0;
      return sum + planned * (row.requiredPerComboUnits || 0);
    }, 0);
  }, [ingredientComboPredictions, comboPlan]);

  const remainingAllIngredient = useMemo(() => {
    return Math.max(selectedIngredientStock - totalReservedIngredient, 0);
  }, [selectedIngredientStock, totalReservedIngredient]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingTitle}>Loading prediction data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Production Predictor</h2>
          <div className={styles.subtitle}>
            {activeTab === "products"
              ? "Select a recipe to see how many packets you can make"
              : activeTab === "combos"
                ? "Select a combo to see how many combos you can make"
                : "Select an ingredient to see what it can produce"}
          </div>
        </div>

        <button className={styles.refreshButton} onClick={fetchData}>
          Refresh
        </button>
      </div>

      {errorMessage ? (
        <div className={styles.errorBanner}>
          <div>{errorMessage}</div>
          <button
            className={styles.dismissButton}
            onClick={() => setErrorMessage("")}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      ) : null}

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeTab === "products" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("products")}
        >
          Products
        </button>
        <button
          className={`${styles.tab} ${activeTab === "combos" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("combos")}
        >
          Combos
        </button>
        <button
          className={`${styles.tab} ${activeTab === "ingredients" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("ingredients")}
        >
          Ingredients
        </button>
      </div>

      {activeTab === "products" ? (
        <>
          <div className={styles.selectionPanel}>
            <div className={styles.sectionTitle}>Select Product Recipe</div>
            <button
              className={styles.selectorButton}
              onClick={() => setShowProductModal(true)}
            >
              <span className={styles.selectorText}>{selectedProductName}</span>
              <span className={styles.chevron}>▾</span>
            </button>
          </div>

          {showProductModal ? (
            <div className={styles.modalOverlay} onClick={() => setShowProductModal(false)}>
              <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <div className={styles.modalTitle}>Select Product</div>
                  <button
                    className={styles.modalCloseButton}
                    onClick={() => setShowProductModal(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className={styles.modalList}>
                  {productChoices.map((item) => (
                    <button
                      key={item.id || "placeholder"}
                      className={`${styles.modalItem} ${
                        selectedProduct === item.id && !item.isPlaceholder
                          ? styles.modalItemSelected
                          : ""
                      } ${item.isPlaceholder ? styles.modalItemPlaceholder : ""}`}
                      onClick={() => handleProductSelect(item.id)}
                    >
                      <span>{item.name}</span>
                      {selectedProduct === item.id && !item.isPlaceholder ? (
                        <span className={styles.checkmark}>✓</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {selectedRecipe ? (
            <div className={styles.resultsPanel}>
              <div className={styles.maxDisplay}>
                <div className={styles.maxLabel}>Maximum Packets Possible</div>
                <div className={styles.maxNumber}>{maxPackets}</div>
                <div className={styles.maxUnit}>packets</div>
              </div>

              <div className={styles.listPanel}>
                <div className={styles.sectionTitle}>Ingredient Analysis</div>

                {predictions.map((p, idx) => (
                  <div
                    key={`${p.name}-${idx}`}
                    className={styles.card}
                    style={{ borderLeftColor: getStatusColor(p.status) }}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle}>{p.name}</div>
                      <div
                        className={styles.statusBadge}
                        style={{ backgroundColor: getStatusColor(p.status) }}
                        title={p.status}
                      >
                        {getStatusIcon(p.status)}
                      </div>
                    </div>

                    <div className={styles.cardRows}>
                      <div className={styles.row}>
                        <div className={styles.label}>Required:</div>
                        <div className={styles.value}>
                          {p.required} {p.requiredUnit}
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.label}>Available:</div>
                        <div className={styles.value}>
                          {p.available} {p.availableUnit}
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.label}>Max Packets:</div>
                        <div className={styles.value}>
                          {p.maxPackets === Infinity ? "∞" : p.maxPackets}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {maxPackets === 0 && predictions.length > 0 ? (
                  <div className={styles.alertBox}>
                    <div className={styles.alertTitle}>Cannot produce any packets</div>
                    <div className={styles.alertMessage}>
                      One or more ingredients are missing or insufficient.
                    </div>
                  </div>
                ) : null}

                {maxPackets > 0 ? (
                  <div className={styles.successBox}>
                    <div className={styles.successTitle}>Production Ready</div>
                    <div className={styles.successMessage}>
                      You can produce up to {maxPackets} packets with your current stock levels.
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔮</div>
              <div className={styles.emptyTitle}>Ready to Predict Production</div>
              <div className={styles.emptyMessage}>
                Select a product from the dropdown above to see how many packets you can make.
              </div>
            </div>
          )}
        </>
      ) : null}

      {activeTab === "combos" ? (
        <>
          <div className={styles.selectionPanel}>
            <div className={styles.sectionTitle}>Select Combo</div>
            <button className={styles.selectorButton} onClick={() => setShowComboModal(true)}>
              <span className={styles.selectorText}>{selectedComboName}</span>
              <span className={styles.chevron}>▾</span>
            </button>
          </div>

          {showComboModal ? (
            <div className={styles.modalOverlay} onClick={() => setShowComboModal(false)}>
              <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <div className={styles.modalTitle}>Select Combo</div>
                  <button
                    className={styles.modalCloseButton}
                    onClick={() => setShowComboModal(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className={styles.modalList}>
                  {comboChoices.map((item) => (
                    <button
                      key={item.id || "placeholder"}
                      className={`${styles.modalItem} ${
                        selectedCombo === item.id && !item.isPlaceholder
                          ? styles.modalItemSelected
                          : ""
                      } ${item.isPlaceholder ? styles.modalItemPlaceholder : ""}`}
                      onClick={() => handleComboSelect(item.id)}
                    >
                      <span>{item.name}</span>
                      {selectedCombo === item.id && !item.isPlaceholder ? (
                        <span className={styles.checkmark}>✓</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {selectedCombo && comboPredictions.length > 0 ? (
            <div className={styles.resultsPanel}>
              <div className={styles.maxDisplay}>
                <div className={styles.maxLabel}>Maximum Combos Possible</div>
                <div className={styles.maxNumber}>{maxCombos}</div>
                <div className={styles.maxUnit}>combos</div>
              </div>

              <div className={styles.listPanel}>
                <div className={styles.sectionTitle}>Product Availability</div>

                {comboPredictions.map((p, idx) => (
                  <div key={`${p.productId}-${idx}`} className={styles.card}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle}>{p.productName}</div>
                      <div className={styles.statusInline} style={{ color: getStatusColor(p.status) }}>
                        {getStatusIcon(p.status)}
                      </div>
                    </div>

                    <div className={styles.cardRows}>
                      <div className={styles.row}>
                        <div className={styles.label}>Required per Combo:</div>
                        <div className={styles.value}>{p.requiredPerCombo} packets</div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.label}>Max Recipe Packets:</div>
                        <div className={styles.value}>
                          {typeof p.maxPacketsCanMake === "number"
                            ? Number.isFinite(p.maxPacketsCanMake)
                              ? p.maxPacketsCanMake
                              : "∞"
                            : 0}{" "}
                          packets
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.label}>Max Combos from Recipe:</div>
                        <div className={styles.value}>
                          {typeof p.maxCombos === "number"
                            ? Number.isFinite(p.maxCombos)
                              ? p.maxCombos
                              : "∞"
                            : 0}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {maxCombos === 0 && comboPredictions.length > 0 ? (
                  <div className={styles.alertBox}>
                    <div className={styles.alertTitle}>Cannot create any combos</div>
                    <div className={styles.alertMessage}>
                      One or more products are missing or insufficient.
                    </div>
                  </div>
                ) : null}

                {maxCombos > 0 ? (
                  <div className={styles.successBox}>
                    <div className={styles.successTitle}>Combo Ready</div>
                    <div className={styles.successMessage}>
                      You can create up to {maxCombos} combos with your current product stock levels.
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📦</div>
              <div className={styles.emptyTitle}>Ready to Predict Combos</div>
              <div className={styles.emptyMessage}>
                Select a combo from the dropdown above to see how many combos you can make.
              </div>
            </div>
          )}
        </>
      ) : null}

      {activeTab === "ingredients" ? (
        <>
          <div className={styles.selectionPanel}>
            <div className={styles.sectionTitle}>Select Ingredient</div>
            <button
              className={styles.selectorButton}
              onClick={() => setShowIngredientModal(true)}
            >
              <span className={styles.selectorText}>{selectedIngredientName}</span>
              <span className={styles.chevron}>▾</span>
            </button>

            <button
              className={`${styles.exportButton} ${exporting ? styles.exportButtonDisabled : ""}`}
              onClick={exportAllIngredientsToXLS}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export All Ingredients (XLS)"}
            </button>
          </div>

          {showIngredientModal ? (
            <div className={styles.modalOverlay} onClick={() => setShowIngredientModal(false)}>
              <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <div className={styles.modalTitle}>Select Ingredient</div>
                  <button
                    className={styles.modalCloseButton}
                    onClick={() => setShowIngredientModal(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className={styles.modalList}>
                  {ingredientChoices.map((item) => (
                    <button
                      key={item.id || "placeholder"}
                      className={`${styles.modalItem} ${
                        selectedIngredient === item.id && !item.isPlaceholder
                          ? styles.modalItemSelected
                          : ""
                      } ${item.isPlaceholder ? styles.modalItemPlaceholder : ""}`}
                      onClick={() => handleIngredientSelect(item.id)}
                    >
                      <span>{item.name}</span>
                      {selectedIngredient === item.id && !item.isPlaceholder ? (
                        <span className={styles.checkmark}>✓</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {selectedIngredient ? (
            <div className={styles.resultsPanel}>
              <div className={styles.listPanel}>
                <div className={styles.sectionTitle}>
                  Products Using This Ingredient ({ingredientProductPredictions.length})
                </div>

                <div className={styles.subtleNote}>
                  Parallel mode: splits this ingredient evenly across all products listed.
                </div>
                <div className={styles.subtleNote}>
                  Total packets (parallel): {totalProductPacketsParallel}
                </div>

                {ingredientProductPredictions.length === 0 ? (
                  <div className={styles.emptyMessage}>No product recipes currently use this ingredient.</div>
                ) : (
                  ingredientProductPredictions.map((row) => (
                    <div
                      key={row.productId}
                      className={styles.card}
                      style={{ borderLeftColor: getStatusColor(row.status) }}
                    >
                      <div className={styles.cardHeader}>
                        <div className={styles.cardTitle}>{row.productName}</div>
                        <div
                          className={styles.statusBadge}
                          style={{ backgroundColor: getStatusColor(row.status) }}
                          title={row.status}
                        >
                          {getStatusIcon(row.status)}
                        </div>
                      </div>

                      <div className={styles.cardRows}>
                        <div className={styles.row}>
                          <div className={styles.label}>Required per Packet:</div>
                          <div className={styles.value}>
                            {row.required} {row.requiredUnit}
                          </div>
                        </div>
                        <div className={styles.row}>
                          <div className={styles.label}>Available (total):</div>
                          <div className={styles.value}>
                            {row.available} {row.availableUnit}
                          </div>
                        </div>
                        <div className={styles.row}>
                          <div className={styles.label}>Max Packets (parallel):</div>
                          <div className={styles.value}>
                            {row.maxPackets === Infinity ? "∞" : row.maxPackets}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={styles.listPanel}>
                <div className={styles.sectionTitle}>
                  Combos Using This Ingredient ({ingredientComboPredictions.length})
                </div>
                <div className={styles.subtleNote}>
                  Parallel mode: splits this ingredient evenly across all combos listed.
                </div>
                <div className={styles.subtleNote}>Total combos (parallel): {totalComboParallel}</div>

                {ingredientComboPredictions.length === 0 ? (
                  <div className={styles.emptyMessage}>No combos currently use this ingredient.</div>
                ) : (
                  ingredientComboPredictions.map((row) => {
                    const planned = comboPlan[row.comboId] || 0;
                    const availableAfterOthers = Math.max(
                      selectedIngredientStock -
                        (totalReservedIngredient - planned * (row.requiredPerComboUnits || 0)),
                      0
                    );
                    const req = row.requiredPerComboUnits || 0;
                    const maxWithPlan = req > 0 ? Math.floor(availableAfterOthers / req) : Infinity;

                    return (
                      <div
                        key={row.comboId}
                        className={styles.card}
                        style={{ borderLeftColor: getStatusColor(row.status) }}
                      >
                        <div className={styles.cardHeader}>
                          <div className={styles.cardTitle}>{row.comboName}</div>
                          <div
                            className={styles.statusBadge}
                            style={{ backgroundColor: getStatusColor(row.status) }}
                            title={row.status}
                          >
                            {getStatusIcon(row.status)}
                          </div>
                        </div>

                        <div className={styles.cardRows}>
                          <div className={styles.row}>
                            <div className={styles.label}>Required per Combo (this ingredient):</div>
                            <div className={styles.value}>
                              {Math.round((row.requiredPerComboUnits || 0) * 100) / 100} {row.requiredUnitDisplay}
                            </div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.label}>Available (total):</div>
                            <div className={styles.value}>
                              {row.available} {row.availableUnit}
                            </div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.label}>Max Combos (parallel):</div>
                            <div className={styles.value}>
                              {row.maxCombos === Infinity ? "∞" : row.maxCombos}
                            </div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.label}>Plan Combos:</div>
                            <div className={styles.adjustmentRow}>
                              <button
                                className={styles.adjustmentButton}
                                onClick={() => adjustComboPlan(row.comboId, -1)}
                                disabled={planned <= 0}
                                type="button"
                              >
                                −
                              </button>
                              <input
                                className={styles.adjustmentInput}
                                value={planned}
                                onChange={(e) => setComboPlanValue(row.comboId, e.target.value)}
                                type="number"
                                min={0}
                                step={1}
                                inputMode="numeric"
                              />
                              <button
                                className={styles.adjustmentButton}
                                onClick={() => adjustComboPlan(row.comboId, 1)}
                                type="button"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.secondaryLabel}>Max if this plan holds:</div>
                            <div className={styles.value}>{maxWithPlan === Infinity ? "∞" : maxWithPlan}</div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.secondaryLabel}>Remaining ingredient overall:</div>
                            <div className={styles.value}>
                              {Math.round(remainingAllIngredient * 100) / 100} {selectedIngredientUnit}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🧪</div>
              <div className={styles.emptyTitle}>Pick an Ingredient</div>
              <div className={styles.emptyMessage}>
                Select an ingredient to see how many packets each product could make if that ingredient were the only limiting factor.
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
